import React, { useState } from 'react';
import { TextField, Button, Checkbox, FormControlLabel, Link, Grid, Box, Typography, Container, useTheme, createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLoginMutation } from '../../../api/recalc/endpoints/authApiSlice';
import { setCookie } from '../../../auth/handleCookie';
import { getHostname } from '../../../utils/getHostname';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://remoni.com/">
        ReMoni A/S
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}


const getPersistedMode = () => {
  const savedMode = localStorage.getItem('mode');
  return savedMode ? savedMode : 'light';
};


export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [login, { isLoading: isLoadingLogin, isError: isErrorLogin, isSuccess: isSuccessLogin, error }] = useLoginMutation();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();


  const [mode, setMode] = useState(getPersistedMode);
  const defaultTheme = createTheme({ palette: { mode } });

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email) && email !== 'admin' && email !== 'demo') {
      newErrors.email = 'Invalid email address';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 4) {
      newErrors.password = 'Password must be at least 4 characters';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    setErrors(formErrors);
    if (Object.keys(formErrors).length === 0) {
      try {
        const response = await login({ Email: email, Password: password }).unwrap();
        dispatch(showSnackbar({ message: 'Login successful', severity: 'success' }));
        setCookie(`${getHostname()}_auth`, response.AccessToken);
        setCookie(`${getHostname()}_authrefresh`, response.RefreshToken);
        setTimeout(() => {
          navigate('/home');
          window.location.reload();
        }, 1000); // Add a 1 second delay before navigating
      } catch (err) {
        dispatch(showSnackbar({ message: 'Login failed', severity: 'error' }));
      }
    } else {
      dispatch(showSnackbar({ message: 'Please correct the errors', severity: 'error' }));
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          backgroundColor:
            mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Container maxWidth="xs" component="main" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100vh' }}>
          <Box
            sx={{
              width: { sm: '100%', md: '60%' },
              textAlign: { sm: 'left', md: 'center' },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', mt: 16, mb: 4 }}>
              <Typography
                variant="h4"
                className="Re"
                sx={{
                  fontFamily: 'neuropolitical',
                  color: (theme) => theme.palette.mode === 'light' ? 'hsl(0, 0%, 20%)' : 'hsl(0, 0%, 85%)',
                  fontWeight: 'bold'
                }}
              >
                Re
              </Typography>
              <Typography
                variant="h4"
                className="M"
                sx={{
                  fontFamily: 'neuropolitical',
                  color: 'hsl(63, 100%, 40%)',
                  fontWeight: 'bold'
                }}
              >
                M
              </Typography>
              <Typography
                variant="h4"
                className="oni"
                sx={{
                  fontFamily: 'neuropolitical',
                  color: (theme) => theme.palette.mode === 'light' ? 'hsl(0, 0%, 20%)' : 'hsl(0, 0%, 85%)',
                  fontWeight: 'bold'
                }}
              >
                oni
              </Typography>
            </Box>
          </Box>
          {/* <LoginRoundedIcon fontSize="large" sx={{ color: '#eaeaea', mb: 1, mt: 32 }} /> */}
          <Typography component="h1" variant="h5">
            Login
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(errors.email)}
              helperText={errors.email}
              variant='filled'
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '5px',
                },
                '& .MuiFilledInput-root': {
                  borderRadius: '5px',
                  color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'black',
                },
                '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
                  display: 'none',
                },
                '& .MuiInputLabel-root': {
                  color: 'gray',
                },
              }}
            />
            <TextField
              margin="normal"
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(errors.password)}
              helperText={errors.password}
              variant='filled'
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '5px',
                },
                '& .MuiFilledInput-root': {
                  borderRadius: '5px',
                  color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'black',
                },
                '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
                  display: 'none',
                },
                '& .MuiInputLabel-root': {
                  color: 'gray',
                },
              }}
            />
            {/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            /> */}
            <Button
              type="submit"
              fullWidth
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: "hsl(69, 100%, 35%)",
                color: 'hsl(69, 100%, 90%)',
                '&:hover': {
                  backgroundColor: 'hsl(69, 100%, 40%)',
                  borderColor: 'divider',
                  color: 'white',
                }
              }}
              disabled={isLoadingLogin}
            >
              Login
            </Button>
            {/* <Grid container>
              <Grid item xs>
                <Link href="#" variant="body2">
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link href="/sign-up" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid> */}
          </Box>
          <Copyright sx={{ mt: 8, mb: 4 }} />
        </Container>
      </Box>
    </ThemeProvider>
  );
}